import React, { Component, Fragment } from 'react';
import { Container, Nav, Navbar, Row, Col } from 'reactstrap';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { debounce } from 'lodash';
import Sticky from 'react-sticky-el';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { matchPath, withRouter } from 'react-router';
import { autorun } from 'mobx';
import RouterPropTypes from 'react-router-prop-types';
import ReactResizeDetector from 'react-resize-detector';

import NavigationLogo from '../NavigationLogo';
import AccountLinks from '../../account/AccountLinks';
import NavigationMenuRow from '../../navigation/NavigationMenuRow';
import { modelOf } from '../../../prop-types';
import UIStore from '../../../store/UIStore';
import MobileNavigationTab from '../../../types/MobileNavigationTab';
import Icon from '../../common/Icon';
import NavigationNewsletterDropdown from '../NavigationNewsletterDropdown';
import NavigationRegionalSettingsMenu from '../NavigationRegionalSettingsMenu';
import NavigationTopLevelMegaMenu from '../NavigationTopLevelMegaMenu';
import NavigationLinkCarousel from '../NavigationLinkCarousel';
import NavigationCart from '../NavigationCart';
import SectionStore from '../../../store/SectionStore';
import ConfigStore from '../../../store/ConfigStore';
import CategoryStore from '../../../store/CategoryStore';
import globalTranslations from '../../../i18n/globalTranslations';
import NavigationSingleLevelMenuRow from '../../navigation/NavigationSingleLevelMenuRow';
import Paths from '../../../types/Paths';
import MenuStore from '../../../store/MenuStore';
import RouteService from '../../../services/RouteService';
import MobileNavigation from '../MobileNavigation';
import ClickOutside from '../../common/ClickOutside';
import NavigationAssistButton from '../../common/NavigationAssistButton';
import SectionIntegrationType from '../../../types/SectionIntegrationType';
import AccountStore from '../../../store/AccountStore';
import NavigationWishListButton from '../NavigationWishListButton';
import TopNavigationSearchButton from '../TopNavigationSearchButton';
import CommonNavigationMiddle from '../CommonNavigationMiddle';
import QuickSearch from '../../search/QuickSearch';
import LinkType from '../../../types/LinkType';
import CustomNavigationLinkParentType from '../../../types/CustomNavigationLinkParentType';
import CustomNavigationLinkStore from '../../../store/CustomNavigationLinkStore';
import LicensePlateInput from '../../vehicle/LicensePlateInput';
import { transformInternalLink } from '../../../util/url';
import { addParametersToPath } from '../../../util/queryString';

@observer
export class CommonNavigation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isSticky: false,
      nonStickyHeaderHeight: null,
      showSearchOnMobile: false,
      showSearchOnDesktop: false,
      inputHasFocus: false
    };

    this.stickyElement = null;
    this.headerTimeout = null;
  }

  componentDidMount() {
    const { uiStore } = this.props;
    const { showSearchOnDesktop, showSearchOnMobile } = this.props;
    if (!uiStore.isDesktop) {
      // Update header height on page load only if we're on mobile.
      this.updateHeaderHeight();
    }

    // Hide search field when the navbar is hidden on mobile
    autorun(
      () => {
        if (
          this.shouldHideSticky() &&
          showSearchOnMobile &&
          showSearchOnDesktop
        ) {
          this.setState(
            { showSearchOnMobile: false, showSearchOnDesktop: false },
            () => {
              this.updateHeaderHeight();
              this.blurElement();
            }
          );
        }
      },
      { delay: 500 }
    );

    // Attach resize event listener to window since ReactResizeDetector component reports resize event too early from landscape to portrait.
    !uiStore.isDesktop && !window.isSSR && this.attachResizeEventListener();
  }

  componentDidUpdate(_, prevState) {
    const { uiStore } = this.props;
    const { inputHasFocus } = this.state;
    if (!uiStore.isDesktop && prevState.inputHasFocus !== inputHasFocus) {
      inputHasFocus && uiStore.toggleSearch(uiStore.isLandscape);
      !inputHasFocus && uiStore.toggleSearch(false);
      this.updateHeaderHeight();
    }
  }

  componentWillUnmount() {
    const { uiStore } = this.props;
    if (this.headerTimeout) {
      clearTimeout(this.headerTimeout);
    }

    !uiStore.isDesktop &&
      !window.isSSR &&
      window.removeEventListener('resize', this.deviceOrientationHandler);
  }

  attachResizeEventListener = () => {
    window.addEventListener('resize', this.deviceOrientationHandler, false);
  };

  deviceOrientationHandler = () => {
    const { uiStore } = this.props;
    const { inputHasFocus } = this.state;
    inputHasFocus && uiStore.toggleSearch(uiStore.isLandscape);
    this.updateHeaderHeight();
  };

  onResize = () => {
    const { uiStore } = this.props;
    const { showSearchOnMobile } = this.state;

    if (!showSearchOnMobile || uiStore.isDesktop) {
      this.maybeResetSearchVisibilityState();
      this.updateHeaderHeight();
    }
  };

  maybeResetSearchVisibilityState = () => {
    if (this.props.uiStore.isDesktop) {
      this.setState({
        showSearchOnMobile: false
      });
    } else {
      this.setState({
        showSearchOnDesktop: false
      });
    }
  };

  updateHeaderHeight = () => {
    const { uiStore, configStore } = this.props;
    const { isSticky, showSearchOnMobile, showSearchOnDesktop } = this.state;
    if (this.stickyElement) {
      const currentHeaderHeight = this.stickyElement.wrapperEl.clientHeight;
      uiStore.setHeaderHeight(currentHeaderHeight);
      const showSearch = !showSearchOnMobile && !showSearchOnDesktop;

      if (!isSticky && !this.shouldShowMobileMenu() && showSearch) {
        this.setState({
          nonStickyHeaderHeight: currentHeaderHeight
        });
      }

      if (configStore.topNavigation.moveSearchbarTopNavigation && !showSearch) {
        this.setState({
          nonStickyHeaderHeight: currentHeaderHeight
        });
      }
    }
  };

  handleVehicleSearch = (licensePlate) => {
    const { routeService, history } = this.props;
    const licensePlateUrl = licensePlate
      ? addParametersToPath(Paths.VehiclePartSearch, {
          id: licensePlate
        })
      : Paths.VehiclePartSearch;
    const url = routeService.getPath(licensePlateUrl);

    history.push(url);
  };

  onKeyPress = () => {
    this.blurElement();
  };

  onInputFocus = () => {
    this.setState({ inputHasFocus: true });
  };

  onInputBlur = () => {
    this.blurElement();
  };

  shouldShowFullHeader = () => {
    const { uiStore } = this.props;

    return (
      uiStore.isDesktop &&
      !this.searchIsFocused() &&
      !uiStore.scrollingDown &&
      uiStore.documentScrollTop < uiStore.headerHeight + 500 &&
      uiStore.documentScrollTop !== 0 &&
      !this.shouldShowMobileMenu() &&
      !this.shouldShowMobileModal()
    );
  };

  shouldHideSticky = () => {
    const { uiStore } = this.props;

    return (
      !uiStore.isDesktop &&
      !this.searchIsFocused() &&
      uiStore.scrollingDown &&
      uiStore.documentScrollTop > uiStore.headerHeight &&
      !this.shouldShowMobileMenu() &&
      !this.shouldShowMobileModal()
    );
  };

  shouldHideStickyShoppingCenter = () => {
    const { uiStore } = this.props;

    return (
      uiStore.isDesktop &&
      !this.searchIsFocused() &&
      uiStore.scrollingDown &&
      uiStore.documentScrollTop > uiStore.headerHeight + 300 &&
      !this.shouldShowMobileMenu() &&
      !this.shouldShowMobileModal()
    );
  };

  shouldHideMixedContent = () => {
    const { configStore, sectionStore } = this.props;

    return (
      configStore.account.isMoneyTransfer ||
      (configStore.zoovu.enabled &&
        sectionStore.activeSection &&
        sectionStore.activeSection.integration_type ===
          SectionIntegrationType.ZOOVU)
    );
  };

  searchIsFocused = () => this.state.inputHasFocus;

  shouldShowMobileMenu = () => {
    const { uiStore } = this.props;
    return !uiStore.isDesktop && uiStore.mobileNavigation.isOpen;
  };

  shouldShowMobileModal = () => {
    const { uiStore } = this.props;
    return uiStore.popoverActive;
  };

  onFixedToggle = (wasSticky) => {
    this.setState(
      {
        isSticky: !wasSticky
      },
      () => {
        // Use setTimeout to wait for renders so that the header has been updated.
        this.headerTimeout = setTimeout(() => {
          this.updateHeaderHeight();
        });
      }
    );
  };

  getAdditionalLinks = () => {
    const { routeService, accountStore, configStore } = this.props;
    const additionalLinks = [];

    if (accountStore.isRetailer) {
      additionalLinks.push({
        id: '_customerProducts',
        label: (
          <FormattedMessage {...globalTranslations.customerProductsTitle} />
        ),
        path: routeService.getPath(Paths.CustomerProducts)
      });
    }

    if (configStore.sidebar.showOnSale) {
      additionalLinks.push({
        id: '_onSale',
        label: <FormattedMessage {...globalTranslations.onSaleTitle} />,
        path: routeService.getPath(Paths.OnSale)
      });
    }
    if (configStore.sidebar.showNewProducts) {
      additionalLinks.push({
        id: '_newProducts',
        label: <FormattedMessage {...globalTranslations.newProductsTitle} />,
        path: routeService.getPath(Paths.NewProducts)
      });
    }

    if (configStore.sidebar.showSoldProducts) {
      additionalLinks.push({
        id: '_soldProducts',
        label: <FormattedMessage {...globalTranslations.soldProductsTitle} />,
        path: routeService.getPath(Paths.SoldProducts)
      });
    }

    if (configStore.sidebar.showMostSold) {
      additionalLinks.push({
        id: '_mostSold',
        label: <FormattedMessage {...globalTranslations.mostPopularTitle} />,
        path: routeService.getPath(Paths.PopularProducts)
      });
    }

    if (configStore.sidebar.showRecommended) {
      additionalLinks.push({
        id: '_recommended',
        label: <FormattedMessage {...globalTranslations.weRecommendTitle} />,
        path: routeService.getPath(Paths.RecommendedProducts)
      });
    }

    return additionalLinks;
  };

  getBottomBarLinks = () => {
    const { sectionStore, categoryStore, uiStore, configStore } = this.props;

    let items;
    const closeMegaMenu = () => uiStore.megaMenu.close();

    if (configStore.activateSections) {
      items = sectionStore.activeSections.map((section) => {
        const toggleSection =
          section.mainCategoryIds && section.mainCategoryIds.length > 0
            ? () => uiStore.megaMenu.toggle(section)
            : () => uiStore.megaMenu.close();
        const debouncedToggleSection = () => this.debouncedHover(toggleSection);
        const onMouseOut = () => this.debouncedHover(closeMegaMenu);

        const path = this.getCategoryOrSectionPath(section);
        return {
          id: section.id,
          label: (
            <span
              onClick={closeMegaMenu}
              onMouseOver={debouncedToggleSection}
              onMouseOut={onMouseOut}
            >
              {section.navigationName}
            </span>
          ),
          path,
          className: classNames({
            open:
              uiStore.megaMenu.activeSection &&
              uiStore.megaMenu.activeSection.id === section.id
          }),
          active: sectionStore.activeSection === section,
          children: [],
          type: section.link_type,
          customNavLinks: this.getCustomNavigationLinks(
            section,
            CustomNavigationLinkParentType.SECTION
          )
        };
      });
    } else {
      items = categoryStore.categories.map((category) => {
        const toggleCategory =
          category.children && category.children.length > 0
            ? () => uiStore.megaMenu.toggle(category)
            : null;
        const onMouseOver = () => this.debouncedHover(toggleCategory);
        const onMouseOut = () => this.debouncedHover(closeMegaMenu);
        const activeMainCategory =
          categoryStore.activeCategory &&
          categoryStore.activeCategory.mainCategory;

        const path = this.getCategoryOrSectionPath(category);
        return {
          id: category.id,
          label: (
            <span
              onClick={closeMegaMenu}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
            >
              {category.navigationName}
            </span>
          ),
          path,
          className: classNames({
            open:
              uiStore.megaMenu.rootCategory &&
              uiStore.megaMenu.rootCategory.id === category.id
          }),
          active: activeMainCategory === category,
          children: [],
          type: category.link_type,
          customNavLinks: this.getCustomNavigationLinks(
            category,
            CustomNavigationLinkParentType.CATEGORY
          )
        };
      });
    }

    let additionalLinks = this.getAdditionalLinks();

    if (configStore.megaMenu.groupAdditionalLinks) {
      additionalLinks = this.groupAdditionalLinks(additionalLinks);
    }

    return items.concat(additionalLinks);
  };

  getCategoryOrSectionPath = (categoryOrSection) => {
    const { routeService } = this.props;

    if (categoryOrSection.link_type === LinkType.EXTERNAL_LINK) {
      return categoryOrSection.path;
    } else if (categoryOrSection.link_type === LinkType.INTERNAL_LINK) {
      return routeService.getPath(
        transformInternalLink(categoryOrSection.path)
      );
    } else {
      return routeService.getPath(categoryOrSection.path);
    }
  };

  getCustomNavigationLinks = (categoryOrSection, type) => {
    const { customNavigationLinkStore } = this.props;

    return type === CustomNavigationLinkParentType.CATEGORY
      ? customNavigationLinkStore.filterByCategoryId(categoryOrSection.id)
      : customNavigationLinkStore.filterBySectionId(categoryOrSection.id);
  };

  groupAdditionalLinks = (links) => {
    // No need to group, if only one item.
    if (links.length <= 1) {
      return links;
    }

    return [
      {
        id: '_customerProducts',
        label: (
          <FormattedMessage {...globalTranslations.additionalLinksTitle} />
        ),
        children: links,
        dropdown: true
      }
    ];
  };

  convertMenuToNavigationEntity = (menu) => {
    let label;

    if (typeof menu.title === 'string') {
      label = (
        <div
          className="CommonNavigation__html-content"
          dangerouslySetInnerHTML={{
            __html: menu.title
          }}
        />
      );
    } else if (menu.title != null) {
      label = menu.title;
    } else {
      label = '';
    }

    return {
      id: menu.id,
      path: menu.url,
      type: menu.type,
      image: menu.image,
      label,
      children: menu.children.map(this.convertMenuToNavigationEntity)
    };
  };

  getMenuBarLinks = () => {
    const { menuStore, routeService } = this.props;

    let items = menuStore.menus.map(this.convertMenuToNavigationEntity);

    items.unshift({
      id: '_top',
      path: routeService.getPath(Paths.FrontPage),
      type: LinkType.INTERNAL_LINK,
      image: null,
      label: (
        <Fragment>
          <Icon name="home" />
          <FormattedMessage {...globalTranslations.homeTitle} />
        </Fragment>
      )
    });

    return items;
  };

  toggleSearch = () => {
    this.updateState('showSearchOnMobile');
    this.updateState('showSearchOnDesktop');
  };

  updateState = (stateVariable) => {
    this.setState(
      (prevState) => ({
        [stateVariable]: !prevState[stateVariable]
      }),
      () => this.focusElement()
    );
  };

  focusElement = () => {
    const { showSearchOnMobile, showSearchOnDesktop } = this.state;
    if ((showSearchOnMobile, showSearchOnDesktop)) {
      const element = this.getAutosuggestInput();

      if (element) {
        element.focus();
      }
    }
  };

  blurElement = () => {
    const element = this.getAutosuggestInput();

    if (element) {
      element.blur();
    }

    this.setState({
      inputHasFocus: false
    });
  };

  getDefaultMobileMenuTab = () => {
    const { location } = this.props;

    if (
      matchPath(location.pathname, {
        path: '/:locale?' + Paths.InfoPage
      })
    ) {
      return MobileNavigationTab.INFO;
    }

    return MobileNavigationTab.PRODUCTS;
  };

  // This debounced function allows us to have a single timeout for all hover
  // (onMouseOut and onMouseOver) callbacks so that only the last one is called.
  //
  // This allows us to
  // 1) prevent showing MegaMenu when the user hovers over the menubar quickly
  // 2) prevent hiding MegaMenu when the user moves the mouse from the menubar
  //    to the actual MegaMenu. This is achieved by having a separate onMouseOver
  //    in the MegaMenu that overrides the last onMouseOut callback.
  //
  debouncedHover = debounce((callback) => {
    if (callback) {
      callback();
    }
  }, 200);

  getTopOffset = () => {
    const { configStore, uiStore } = this.props;
    const useStickyHeaderOnStartup =
      uiStore.isDesktop && configStore.topNavigation.useStickyHeaderOnStartup;

    if (useStickyHeaderOnStartup) {
      return 0;
    }

    return uiStore.isDesktop && !uiStore.searchIsActive ? 100 : 0;
  };

  renderNavigationTop = () => {
    const { configStore, uiStore } = this.props;

    const hideCheckout =
      !configStore.topNavigation.hideCheckout && uiStore.isDesktop;
    const moveShoppingCartLoginRight = !configStore.topNavigation
      .moveShoppingCartLoginRight;

    return (
      <Navbar className="CommonNavigation__top d-none d-lg-block">
        <Container className="CommonNavigation__top-container">
          <div className="CommonNavigation__top-inner">
            <NavigationMenuRow items={this.getMenuBarLinks()} />
            <div
              className={classNames(
                !moveShoppingCartLoginRight &&
                  'CommonNavigation__top-right-navigation-shopping-buttons-wrapper'
              )}
            >
              <Nav className="CommonNavigation__top-right-navigation">
                {configStore.topNavigation.moveSearchbarTopNavigation && (
                  <TopNavigationSearchButton
                    onShowSearchOnTopNavigation={this.toggleSearch}
                  />
                )}
                <NavigationNewsletterDropdown />
                <NavigationRegionalSettingsMenu />
                {configStore.wishlist.enabled && <NavigationWishListButton />}
                {configStore.siteConfig.isWebStore && <AccountLinks />}
              </Nav>
              {!moveShoppingCartLoginRight && (
                <NavigationCart
                  className={'NavigationCart__right-side-shopping-button'}
                  withCheckoutButton={hideCheckout}
                />
              )}
            </div>
          </div>
          {configStore.topNavigation.moveSearchbarTopNavigation &&
            this.state.showSearchOnDesktop && (
              <Row className="justify-content-center pb-2">
                {configStore.vehiclePartSearch.enabled && (
                  <div>
                    <LicensePlateInput
                      handleOnSubmit={this.handleVehicleSearch}
                      clearAfterSubmit
                    />
                  </div>
                )}
                <Col sm="5">
                  <QuickSearch
                    handleFocus={this.onInputFocus}
                    handleBlur={this.onInputBlur}
                    toggleSearch={() => null}
                  />
                </Col>
              </Row>
            )}
        </Container>
      </Navbar>
    );
  };

  renderNavigationTopBalance = () => {
    return (
      <Navbar className="CommonNavigation__top d-none d-lg-block">
        <Container className="CommonNavigation__top-container">
          <div className="CommonNavigation__top-inner">
            <NavigationMenuRow items={this.getMenuBarLinks()} />
            <AccountLinks />
          </div>
        </Container>
      </Navbar>
    );
  };

  renderNavigationMiddle = () => {
    const { configStore, uiStore } = this.props;
    const { isSticky, showSearchOnMobile, inputHasFocus } = this.state;

    const hideCheckout =
      !configStore.topNavigation.hideCheckout && uiStore.isDesktop;
    const moveShoppingCartLoginRight = !configStore.topNavigation
      .moveShoppingCartLoginRight;
    const useStickyHeaderOnStartup =
      uiStore.isDesktop && configStore.topNavigation.useStickyHeaderOnStartup;
    const ifShouldShowFullHeader = this.shouldShowFullHeader();

    return (
      <Navbar className="CommonNavigation__middle" expand="lg">
        <Container className="CommonNavigation__middle-container">
          <CommonNavigationMiddle
            hideCheckout={hideCheckout}
            moveShoppingCartLoginRight={moveShoppingCartLoginRight}
            isSticky={isSticky}
            onShowSearchOnMobile={this.toggleSearch}
            onSearchKeyPress={this.onKeyPress}
            inputFocus={this.onInputFocus}
            inputBlur={this.onInputBlur}
            inputHasFocus={inputHasFocus}
            shouldShowSearchOnMobile={showSearchOnMobile}
            useStickyHeaderOnStartup={useStickyHeaderOnStartup}
            ifShouldShowFullHeader={ifShouldShowFullHeader}
          />
        </Container>
      </Navbar>
    );
  };

  renderNavigationBottom = () => {
    const { configStore } = this.props;

    return (
      <Fragment>
        <NavigationSingleLevelMenuRow
          items={this.getBottomBarLinks()}
          className="CommonNavigation__bottom"
        />
        {configStore.mainNavigation.useMegamenu && (
          <NavigationTopLevelMegaMenu debouncer={this.debouncedHover} />
        )}
      </Fragment>
    );
  };

  getAutosuggestInput = () => {
    return document.querySelector('.CommonNavigation .QuickSearch input');
  };

  render() {
    const { configStore, uiStore } = this.props;
    const { nonStickyHeaderHeight } = this.state;

    const hideAssistantMenu =
      configStore.mobileSettings.showAssistantMenu === '0';
    const useStickyHeaderOnStartup =
      uiStore.isDesktop && configStore.topNavigation.useStickyHeaderOnStartup;
    const hideHeaderOnScrollingDown =
      uiStore.isDesktop && configStore.topNavigation.hideHeaderOnScrollingDown;
    const ifShouldShowFullHeader = this.shouldShowFullHeader();

    return (
      <div>
        {uiStore.isDesktop && <NavigationLinkCarousel />}
        <Sticky
          className={classNames('CommonNavigation', {
            'CommonNavigation--show-full-header':
              !useStickyHeaderOnStartup && ifShouldShowFullHeader,
            'CommonNavigation--hide-sticky': this.shouldHideSticky(),
            'CommonNavigation--hide-sticky-shoppingCenter':
              hideHeaderOnScrollingDown &&
              this.shouldHideStickyShoppingCenter(),
            'CommonNavigation--scroll-logo': useStickyHeaderOnStartup,
            'CommonNavigation--mobile-navigation-open': this.shouldShowMobileMenu(),
            'CommonNavigation--forced-static': !!window.isSSR
          })}
          name="top"
          topOffset={this.getTopOffset()}
          onFixedToggle={this.onFixedToggle}
          ref={(ref) => (this.stickyElement = ref)}
          holderProps={{
            className: 'CommonNavigation__sticky-placeholder',
            style: {
              height: nonStickyHeaderHeight && nonStickyHeaderHeight + 'px'
            }
          }}
        >
          {!configStore.siteConfig.isHomePage && this.renderNavigationTop()}
          {configStore.siteConfig.isHomePage &&
            configStore.account.isMoneyTransfer &&
            this.renderNavigationTopBalance()}
          {this.renderNavigationMiddle()}
          {configStore.siteConfig.isShoppingCenter &&
            uiStore.isDesktop && (
              <div className="CommonNavigation__shopping-center--separator" />
            )}
          {this.shouldShowMobileMenu() && (
            <ClickOutside
              onClickOutside={uiStore.mobileNavigation.toggle}
              excludedSelector=".MobileNavigationButton, .modal"
            >
              <MobileNavigation
                shouldHideProductContent={this.shouldHideMixedContent()}
              />
            </ClickOutside>
          )}
          {uiStore.isDesktop &&
            !this.shouldHideMixedContent() &&
            !configStore.mainNavigation.useSidebar &&
            this.renderNavigationBottom()}
        </Sticky>
        {!uiStore.isDesktop && (
          <Fragment>
            {!this.shouldHideMixedContent() && (
              <Container className="CommonNavigation__mobile-wide-container">
                <div className="CommonNavigation__mobile-link-carousel">
                  <NavigationLinkCarousel />
                </div>
              </Container>
            )}
            {configStore.mobileSettings.showStoreLogo && (
              <Container className="CommonNavigation__mobile-logo-container">
                <div className="CommonNavigation__mobile-logo">
                  <NavigationLogo />
                </div>
              </Container>
            )}
            {!this.shouldHideMixedContent() &&
              hideAssistantMenu && (
                <Container className="CommonNavigation__mobile-assist-container">
                  <div className="CommonNavigation__mobile-assist">
                    <NavigationAssistButton
                      tab={this.getDefaultMobileMenuTab()}
                    />
                  </div>
                </Container>
              )}
          </Fragment>
        )}
        <ReactResizeDetector
          handleHeight
          onResize={this.onResize}
          refreshMode="throttle"
          refreshRate={100}
          refreshOptions={{ leading: true, trailing: true }}
          querySelector=".CommonNavigation, .NavigationLinkCarousel"
        />
      </div>
    );
  }
}

CommonNavigation.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  menuStore: modelOf(MenuStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  customNavigationLinkStore: modelOf(CustomNavigationLinkStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  location: RouterPropTypes.location.isRequired
};

export default inject(
  'accountStore',
  'categoryStore',
  'configStore',
  'menuStore',
  'sectionStore',
  'uiStore',
  'customNavigationLinkStore',
  'routeService'
)(withRouter(CommonNavigation));



// WEBPACK FOOTER //
// ./src/components/header/CommonNavigation/index.jsx